import { environment } from '@/consts'

// Third-party marketing/analytics tags that used to live inline in
// backend/templates/frontend.html. They are injected here instead so we can
// gate them on (a) the visitor being unauthenticated and (b) consent.
//
// GTM container GTM-NBG23LD is opaque to this repo: it may hold GA4 (analytics)
// as well as ad/marketing tags. We therefore load Google Consent Mode with a
// default-denied state BEFORE injecting GTM and update it from the user's
// choice, so consent-aware tags inside the container self-suppress. Tags that
// are NOT configured for Consent Mode still fire once GTM loads — auditing and
// configuring the container for consent is the remaining GTM-side follow-up.

const GTM_CONTAINER_ID = 'GTM-NBG23LD'
const APOLLO_APP_ID = '66316cf25584350423a8e981'

let gtmInjected = false
let apolloInjected = false
let consentModeInitialized = false

export const hasLoadedTracking = () => gtmInjected || apolloInjected

const isBrowser = () => typeof window !== 'undefined'

// frontend2 authenticates with a DRF token in localStorage, so this is the only
// reliable client-side signal of whether the visitor is signed in. Third-party
// tags must not run inside the authenticated product.
export const isAuthenticated = () => isBrowser() && !!window.localStorage.getItem('token')

// Must push the `arguments` object, not an array: GTM only recognises a
// dataLayer entry as a gtag command (e.g. `consent`) when it is an Arguments
// object. An array is treated as an ordinary message and the command is
// silently ignored.
function gtag() {
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

const initConsentMode = () => {
  if (consentModeInitialized) return
  consentModeInitialized = true
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
}

const updateConsentMode = (consent) => {
  initConsentMode()
  gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
  })
}

const injectGtm = () => {
  if (gtmInjected) return
  gtmInjected = true
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`
  document.head.appendChild(script)
}

const injectApollo = () => {
  if (apolloInjected) return
  apolloInjected = true
  const script = document.createElement('script')
  script.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${Math.random()
    .toString(36)
    .substring(7)}`
  script.async = true
  script.defer = true
  script.onload = () => {
    window.trackingFunctions?.onLoad({ appId: APOLLO_APP_ID })
  }
  document.head.appendChild(script)
}

// Injects the third-party tags permitted by the given consent, honouring the
// same environment guards the inline template used (GTM: prod+staging, Apollo:
// prod only). No-ops for authenticated users. Idempotent: safe to call on every
// consent change and on bootstrap.
export const applyConsent = (consent) => {
  if (!isBrowser() || !consent) return
  if (isAuthenticated()) return

  const gtmEnv = environment === 'production' || environment === 'staging'
  const apolloEnv = environment === 'production'

  if (gtmEnv && (gtmInjected || consent.analytics || consent.marketing)) {
    updateConsentMode(consent)
    injectGtm()
  }

  if (apolloEnv && consent.marketing) {
    injectApollo()
  }
}
